import React, { useState, useCallback } from 'react';
import FileUpload from './FileUpload';
import FileList from './FileList';
import ProgressBar from './ProgressBar';
import { extractTextFromPdf } from '../services/pdfService';
import { CopyIcon, ExtractIcon, SpinnerIcon, ErrorIcon, CheckIcon } from './icons';
import { useTranslation } from '../i18n';

const ExtractTextTool: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [extractedText, setExtractedText] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const { t } = useTranslation();

  const handleFileSelect = useCallback((selectedFiles: File[]) => {
    if (selectedFiles.length > 0) {
      setFile(selectedFiles[0]);
      setError(null);
      setExtractedText(null);
    }
  }, []);

  const handleRemoveFile = useCallback(() => {
    setFile(null);
  }, []);

  const handleExtractText = async () => {
    if (!file) {
      setError(t('errorSelectFileToExtract'));
      return;
    }
    setIsLoading(true);
    setIsProcessing(false);
    setProgress(0);
    setError(null);
    setExtractedText(null);
    try {
      const result = await extractTextFromPdf(file, setProgress);
      setIsProcessing(true);
      setExtractedText(result.text);
    } catch (err: any) {
      setError(err.message || t('errorUnexpected'));
    } finally {
      setIsLoading(false);
      setIsProcessing(false);
    }
  };

  const handleCopy = () => {
    if (extractedText) {
      navigator.clipboard.writeText(extractedText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="w-full max-w-2xl mx-auto bg-slate-800/50 rounded-2xl shadow-xl p-6 sm:p-8 border border-slate-700">
      <FileUpload onFilesSelect={handleFileSelect} multiple={false} accept=".pdf" />
      
      {file && <FileList files={[file]} onRemoveFile={handleRemoveFile} />}
      
      {isLoading && <ProgressBar progress={progress} isProcessing={isProcessing} />}

      <div className="mt-6">
        <button
          onClick={handleExtractText}
          disabled={isLoading || !file}
          className="w-full flex items-center justify-center gap-3 bg-indigo-600 text-white font-bold py-3 px-4 rounded-lg hover:bg-indigo-700 disabled:bg-indigo-400 disabled:cursor-not-allowed transition-all duration-200"
        >
          {isLoading ? <SpinnerIcon /> : <ExtractIcon />}
          <span>{isLoading ? (isProcessing ? t('statusProcessing') : t('statusUploading')) : t('extractButton')}</span>
        </button>
      </div>

      {error && (
        <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 text-red-400 rounded-lg flex items-center gap-3">
            <ErrorIcon/>
            <span>{error}</span>
        </div>
      )}
      {extractedText !== null && (
        <div className="mt-4">
          <div className="flex justify-between items-center mb-2">
            <h3 className="font-semibold text-lg text-slate-200">{t('extractedTextLabel')}</h3>
            <div className="flex items-center gap-2">
                <button
                onClick={handleCopy}
                className="flex items-center gap-2 text-sm bg-slate-700 hover:bg-slate-600 text-slate-200 font-semibold py-2 px-3 rounded-md transition-colors"
                >
                {copied ? <CheckIcon/> : <CopyIcon/>}
                {copied ? t('copiedButton') : t('copyButton')}
                </button>
            </div>
          </div>
          <textarea
            readOnly
            value={extractedText}
            className="w-full h-64 p-3 bg-slate-900/70 border border-slate-700 rounded-lg text-slate-300 font-mono text-sm resize-y focus:ring-2 focus:ring-indigo-500 focus:outline-none"
            placeholder={t('noTextPlaceholder')}
          />
        </div>
      )}
    </div>
  );
};

export default ExtractTextTool;
